/** Connectors contract — sources with group tags + audited sync (`/api/connectors`). */

import { api } from './api';

export interface SourceInfo {
  id: string;
  kind: string;
  display_name: string;
  readable_groups: string[];
  enabled: boolean;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_docs: number;
  /** Where it reads from: SharePoint site/folder, Confluence space, Bitbucket project/repo. */
  config?: Record<string, string>;
  /** SharePoint: when file permissions were last re-read. */
  permissions_checked_at?: string | null;
}

export interface NewSource {
  id: string;
  kind: 'sharepoint' | 'confluence' | 'bitbucket';
  display_name: string;
  config: Record<string, string>;
  readable_groups: string[];
}

export interface SyncResult {
  source_id: string;
  documents: number;
  chunks: number;
  status: string;
}

export async function fetchSources(): Promise<SourceInfo[]> {
  const res = await api.get<{ sources: SourceInfo[] }>('/connectors');
  return res.sources;
}

export async function patchSource(
  id: string,
  patch: { readable_groups?: string[]; enabled?: boolean },
): Promise<SourceInfo> {
  return api.patch<SourceInfo>(`/connectors/${id}`, patch);
}

export async function createSource(source: NewSource): Promise<SourceInfo> {
  return api.post<SourceInfo>('/connectors', source);
}

export async function syncSource(id: string): Promise<SyncResult> {
  return api.post<SyncResult>(`/connectors/${id}/sync`, {});
}

export async function deleteSource(
  id: string,
): Promise<{ source_id: string; purged_chunks: number }> {
  return api.delete<{ source_id: string; purged_chunks: number }>(`/connectors/${id}`);
}

/** Legacy shape kept for the demo AgentEditor dropdown. */
export interface ConnectorInfo {
  id: string;
  name: string;
  grain: string;
  source_system: string;
}

export async function fetchConnectors(): Promise<ConnectorInfo[]> {
  const sources = await fetchSources();
  return sources.map((s) => ({
    id: s.id,
    name: s.display_name,
    grain: s.kind.includes('github') ? 'code' : 'docs',
    source_system: s.kind.replace('-fixture', ''),
  }));
}
